<template>
  <div class="password-field">
    <fn-form-field-info-wrapper
      :no-side-margin="fullWidth"
      :no-flex-side="fullWidth"
    >
      <template #default>
        <fn-form-field-input
          :id="id"
          v-model:value="model"
          :class="{ 'form-field--bottom-flat': passwordStrengthVisible }"
          :hide-required-asterix="hideRequiredAsterix"
          :name="name"
          :label="label"
          :type="inputType"
          :rules="rules"
          :float-label="floatLabel"
          :autocomplete="autocomplete"
          :action-icon="{
            icon: 'eye',
            size: 'm',
          }"
          :action-icon-submits="false"
          :action-icon-aria-label="$t('accessibility.form.togglePassword')"
          bypass-sanitation
          @action-icon-click="passwordVisible = !passwordVisible"
          @focus="emit('focus', $event)"
          @blur="emit('blur', $event)"
        >
          <template #errors="slotProps">
            <fn-form-validation-info
              v-if="passwordStrengthVisible"
              :rules="validationInfoRules(slotProps.field.value)"
              :errors="slotProps.field.errors"
              :error="slotProps.field.meta.validated && slotProps.field.meta.touched && !slotProps.field.meta.valid"
              :valid="slotProps.field.meta.validated && slotProps.field.meta.touched && slotProps.field.meta.valid"
            />
            <fn-error-message v-if="slotProps.field.errorMessage && slotProps.field.meta.touched">
              {{ translateError(slotProps.field.errorMessage) }}
            </fn-error-message>
          </template>
        </fn-form-field-input>
      </template>
      <template
        v-if="passwordStrengthVisible"
        #side
      >
        <fn-info-i
          class="pf-info"
          :aria-label-open-button="
            $t('accessibility.form.openAdditionalInfoDialog', { field: $t('forms.checkout.fields.password.label') })
          "
          :aria-label-close-button="
            $t('accessibility.form.closeAdditionalInfoDialog', { field: $t('forms.checkout.fields.password.label') })
          "
        >
          {{ $t('forms.checkout.fields.password.info') }}
        </fn-info-i>
      </template>
    </fn-form-field-info-wrapper>
  </div>
</template>

<script setup lang="ts">
import { defineFormFieldPropsDefaults, type FormFieldProps } from '@/mixins/FormField';

const emit = defineEmits(['focus', 'blur']);

withDefaults(
  defineProps<
    FormFieldProps & {
      passwordStrengthVisible?: boolean;
      hideRequiredAsterix: boolean;
      fullWidth?: boolean;
      rules: boolean;
      name: boolean;
    }
  >(),
  {
    ...defineFormFieldPropsDefaults(),
    passwordStrengthVisible: true,
  },
);
const model = defineModel<string>('value');

const { $i18n } = useNuxtApp();
const passwordVisible = ref(false);
const inputType = computed(() => (unref(passwordVisible) ? 'text' : 'password'));

const validations = {
  hasMinimumCharacters: (value: string) => {
    return value?.length >= 8;
  },
  hasMinimumNumbers: (value: string) => {
    return /\d+/.test(value);
  },
  hasMinimumLargeCharacters: (value: string) => {
    return /[A-Z]+/.test(value);
  },
  hasMinimumSmallCharacters: (value: string) => {
    return value ? /[a-z]+/.test(value) : false;
  },
  hasMinimumSpecialCharacters: (value: string) => {
    // eslint-disable-next-line no-useless-escape
    return /.*["^§$%&\/()=!?`´\\}\][{*+~'#\-_.,@€<>|]+.*/.test(value);
  },
};

const validationInfoRules = (value: string) => {
  const rules = [
    { key: 'min8Characters', method: 'hasMinimumCharacters' },
    { key: 'minOneNumber', method: 'hasMinimumNumbers' },
    { key: 'minOneLargeAlpha', method: 'hasMinimumLargeCharacters' },
    { key: 'minOneSmallAlpha', method: 'hasMinimumSmallCharacters' },
    { key: 'minOneSpecialChar', method: 'hasMinimumSpecialCharacters' },
  ];

  return rules.map((rule) => ({
    text: $i18n.t(`forms.password.rules.${rule.key}`),
    valid: validations[rule.method as keyof typeof validations](value),
  }));
};
</script>

<style lang="scss" scoped>
.password-field {
  .form-input-password--full-width {
    width: 100%;
  }

  .pf-toggle {
    color: palette(black);
    position: absolute;
    right: -4px;
    text-decoration: none;
    top: 9px;

    &--full-width {
      right: 5px;
    }

    :deep(.icon--eye) {
      font-size: 24px;
    }
  }

  .pf-info {
    position: relative;
    top: 20px;
    right: 0;
  }

  :deep(.form-field--bottom-flat input) {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
}
</style>
